import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSnapshot } from 'valtio';
import { backendApi, type LadderUser } from '../../@backend';
import { userState } from '../../store';
import { Card } from '../ds/Card';

type RankedUser = LadderUser & {
  position: number;
};

interface PodiumProps {
  winners?: LadderUser[] | string | null;
}

const PODIUM_ORDER = [4, 2, 0, 1, 3];
const COLUMN_HEIGHTS = [210, 172, 138, 112, 92];
const PRIZE_SPLIT = [
  { place: '1. místo', amount: '1 600 Kč', tone: 'bg-gradient-orange text-white' },
  { place: '2. místo', amount: '800 Kč', tone: 'bg-brand-blue/10 text-brand-blue' },
  { place: '3. místo', amount: '500 Kč', tone: 'bg-brand-blue/10 text-brand-blue' },
];

const getInitials = (user: LadderUser) =>
  `${user.firstname?.charAt(0) ?? ''}${user.lastname?.charAt(0) ?? ''}`.toUpperCase() || '?';

const getName = (user: LadderUser) =>
  [user.firstname, user.lastname].filter(Boolean).join(' ') || 'Hráč';

function PlayerAvatar({ user, isWinner }: { user: LadderUser; isWinner: boolean }) {
  const apiUrl = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

  return (
    <div
      className={[
        'relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border-4 bg-bg-input shadow-row',
        isWinner ? 'border-brand-orange' : 'border-bg-card',
      ].join(' ')}
    >
      {user.avatar ? (
        <img
          src={`${apiUrl}/avatars/${user.avatar}`}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="font-nunito text-[16px] font-black text-text-muted">
          {getInitials(user)}
        </span>
      )}
    </div>
  );
}

function PodiumStep({ winner }: { winner: RankedUser }) {
  const rank = winner.position + 1;
  const isWinner = rank === 1;
  const height = COLUMN_HEIGHTS[winner.position] ?? 80;
  const name = getName(winner);

  return (
    <motion.div
      className="flex min-w-0 flex-col items-center justify-end text-center"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: winner.position * 0.08, duration: 0.35 }}
    >
      <div className="mb-3 flex w-full flex-col items-center gap-2 px-1">
        <div className="relative">
          <PlayerAvatar user={winner} isWinner={isWinner} />
          <span
            className={[
              'absolute -bottom-1 -right-1 flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 font-nunito text-[12px] font-black text-white shadow-row',
              isWinner ? 'bg-gradient-orange' : 'bg-gradient-primary',
            ].join(' ')}
          >
            {rank}
          </span>
        </div>

        <div className="min-h-[48px] w-full">
          {winner.publicProfile && winner.id ? (
            <Link
              to={`/uzivatel/profil/${winner.id}`}
              className="line-clamp-2 font-nunito text-[14px] font-black leading-tight text-brand-blue hover:underline"
              title={name}
            >
              {name}
            </Link>
          ) : (
            <p
              className="line-clamp-2 font-nunito text-[14px] font-black leading-tight text-text-primary"
              title={name}
            >
              {name}
            </p>
          )}
          <p className="mt-1 font-nunito text-[12px] font-bold text-text-muted">
            {winner.points ?? 0} bodů
          </p>
        </div>
      </div>

      <motion.div
        className={[
          'flex w-full items-end justify-center rounded-t-card border border-b-0 px-2 pb-3 shadow-row',
          isWinner
            ? 'border-brand-orange/35 bg-gradient-orange'
            : 'border-brand-blue/20 bg-gradient-primary',
        ].join(' ')}
        initial={{ height: 0 }}
        animate={{ height }}
        transition={{ delay: winner.position * 0.08 + 0.12, duration: 0.65, ease: 'easeOut' }}
      >
        <span className="font-nunito text-[18px] font-black text-white">
          {rank}.
        </span>
      </motion.div>
    </motion.div>
  );
}

export default function Podium({ winners }: PodiumProps) {
  const userSnap = useSnapshot(userState);
  const [ladderWinners, setLadderWinners] = useState<RankedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const backend = useMemo(
    () =>
      new backendApi({
        BASE: process.env.REACT_APP_API_URL ?? 'http://localhost:3000',
        TOKEN: typeof userSnap.token === 'string' ? userSnap.token : undefined,
      }),
    [userSnap.token]
  );

  useEffect(() => {
    if (Array.isArray(winners) && winners.length) {
      setLadderWinners(winners.slice(0, 5).map((winner, position) => ({ ...winner, position })));
      return;
    }

    if (!userSnap.isLoggedIn || !userSnap.userProfile.entryFeePaid) {
      return;
    }

    let isMounted = true;

    setIsLoading(true);
    setLoadFailed(false);

    backend.ladder
      .ladderControllerGetAllUsersInLadder()
      .then((resUsers) => {
        if (!isMounted) return;

        setLadderWinners(
          resUsers
            .slice(0, 5)
            .map((winner, position) => ({ ...winner, position }))
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setLoadFailed(true);
        setLadderWinners([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [backend.ladder, userSnap.isLoggedIn, userSnap.userProfile.entryFeePaid, winners]);

  const podium = useMemo(
    () => PODIUM_ORDER.map((position) => ladderWinners[position]).filter(Boolean),
    [ladderWinners]
  );

  if (!userSnap.isLoggedIn || !userSnap.userProfile.entryFeePaid) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card padding="none" className="overflow-hidden">
        <div className="flex flex-col gap-1 border-b border-border-neutral px-5 py-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="tb-label">Vítězové</p>
            <h2 className="tb-h2 text-text-primary">Aktuální špička žebříčku</h2>
          </div>
          <p className="font-nunito text-[13px] font-bold text-text-muted">
            Top {ladderWinners.length || 5} hráčů
          </p>
        </div>

        <div className="bg-bg-card px-4 pb-5 pt-6">
          {isLoading ? (
            <div className="flex h-[320px] items-end justify-center gap-3">
              {[160, 210, 138, 172, 112].map((height, index) => (
                <div key={height} className="flex w-16 animate-pulse flex-col items-center gap-3">
                  <div className="h-14 w-14 rounded-full bg-bg-app" />
                  <div className="h-4 w-14 rounded-full bg-bg-app" />
                  <div
                    className="w-full rounded-t-card bg-bg-app"
                    style={{ height: height - index * 6 }}
                  />
                </div>
              ))}
            </div>
          ) : loadFailed ? (
            <div className="flex h-[180px] items-center justify-center text-center">
              <p className="tb-body text-text-muted">Vítěze se nepodařilo načíst.</p>
            </div>
          ) : podium.length > 0 ? (
            <div className="overflow-x-auto pb-1 scrollbar-thin">
              <div className="grid min-w-[430px] grid-cols-5 items-end gap-2 border-b border-border-neutral px-1 pt-2 md:min-w-0 md:gap-3">
                {podium.map((winner) => (
                  <PodiumStep key={winner.id ?? winner.email} winner={winner} />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-[180px] items-center justify-center text-center">
              <p className="tb-body text-text-muted">Žebříček zatím nemá žádné vítěze.</p>
            </div>
          )}
        </div>
      </Card>

      <Card className="border border-brand-blue/10">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="max-w-xl">
            <p className="tb-label">Díky za hru</p>
            <h3 className="tb-h3 text-text-primary">Děkujeme všem za účast.</h3>
            <p className="tb-body mt-2 text-text-muted">
              Prize money pro letošní hru je 2 900 Kč. Podle historického poměru pro první
              tři místa vychází férové rozdělení následovně.
            </p>
          </div>

          <div className="grid w-full gap-2 sm:grid-cols-3 md:max-w-md">
            {PRIZE_SPLIT.map((prize) => (
              <div
                key={prize.place}
                className={[
                  'rounded-card px-4 py-3 text-center shadow-row',
                  prize.tone,
                ].join(' ')}
              >
                <p className="font-nunito text-[12px] font-extrabold uppercase tracking-wide opacity-80">
                  {prize.place}
                </p>
                <p className="mt-1 font-nunito text-[20px] font-black leading-none">
                  {prize.amount}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card className="border border-brand-orange/20 bg-brand-orange/5">
        <div className="flex flex-col gap-2">
          <p className="tb-label text-brand-orange">Další hra</p>
          <h3 className="tb-h3 text-text-primary">FIFA World Cup 2026</h3>
          <p className="tb-body text-text-muted">
            Další tipovačka poběží během mistrovství světa ve fotbale 2026. Turnaj bude výrazně
            větší, takže počítejte s tím, že bude potřeba natipovat hodně zápasů.
            Příprava turnaje probíhá už teď! Rádi přivítáme nové tváře, které nám pomohou udělat další ročník ještě lepší než ten letošní.
            <br /><br />
            Registrace je otevřena, reset bodů proběhne v následujících dnech.
            FIFA World Cup 2026 se bude konat v Kanadě, Mexiku a USA, takže se máme na co těšit a začíná už 11. června 2026!
          </p>
        </div>
      </Card>
    </div>
  );
}
